import {AllowedUsersModel} from 'library/src/authentication/models/AllowedUsersModel';
import {logger} from 'library/src/core/services/LogService';
import FieldCreation from 'library/src/fields/models/FieldCreation';
import {PixagriField} from 'library/src/fields/models/PixagriField';
import {FieldIrrigationService, FieldVisioCropServiceModel} from 'library/src/fields/models/WagoField';
import {DateTime} from 'luxon';
import {AuthUser} from 'modules/auth/models/AuthUser';
import {BASE_COVER_CREATION} from 'modules/nitrogen/configs/NitrogenConfigs';
import {nitrogenValidationSchema} from 'modules/nitrogen/configs/NitrogenFieldEdit';
import {getRotation} from 'modules/rotations/services/RotationService';
import {isSubscriptionValid} from 'modules/subscriptions/services/isFieldSubscribe';
import {BASE_VISIOCROP_FIELD_USE} from 'modules/visio_crop/configs/VisioCropConfig';
import {visioCropValidationSchema} from 'modules/visio_crop/configs/VisioCropFieldEdit';
import {irrigationValidationSchema} from 'modules/waterBalance/configs/IrrigationFieldEdit';
import {BASE_ROTATION_CREATION} from 'modules/waterBalance/configs/RotationConfig';
import {ServiceId} from 'shared/models/RouterParam';
import {wagoNestApiUrl} from 'shared/services/ApiConfig';
import log from 'shared/services/LogService';
import * as yup from 'yup';
import {ObjectSchema} from 'yup';
import {FieldNitrogenService} from '../../../library/src/nitrogen/models/FieldNitrogenService';
import {baseValidationSchema} from '../configs/FieldEdit';
import FieldsState from '../models/FieldsState';

const SQUARE_METER_TO_HA = 0.0001;

export const replaceFieldId = function (url: string, fieldId: number | string): string {
  return url.replace(/(.*\/fields)\/?(\d*|overview)?(\/.*)?/, `$1/${fieldId}$3`);
};
export const replaceServiceId = function (url: string, serviceId: ServiceId): string {
  return url.replace(/(irrigation|carbon|weather|vegetation)/, serviceId);
};
export const deleteEndUrl = function (url: string): string {
  return url.replace(/(.*\/overview)?(\/.*)?(\/.*)/, `$1$2`);
};
export const getViewId = function (url: string): string {
  return url.replace(/(.*\/)(.*)/, `$2`);
};
export const removeDuplicates = function (noDuplicates: FieldsState['campaigns']) {
  return (noDuplicates = noDuplicates?.filter(
    (campaign, index: any, self: any[]) => self.findIndex((t) => t.campaign_id === campaign.campaign_id) === index,
  ) as FieldsState['campaigns']);
};

/**
 * Compute the area in Ha
 * (Floor to int)
 * @param selectedFields
 * @returns
 */
export const computeAreaHa = (field: PixagriField, floor: boolean): number => {
  if (field.area) {
    let areaHa = field.area * SQUARE_METER_TO_HA;
    if (floor) {
      areaHa = Math.floor(areaHa);
    }
    if (areaHa === 0) {
      log.debug(`areaHa forced to 1 beacause floor areaHA return 0 with areaHa of ${field.area * SQUARE_METER_TO_HA}`);
      return 1;
    } else {
      return areaHa;
    }
  } else {
    log.error(`Field ${field.field_id} does not have a filled area`);
  }
  return 0;
};

/**
 * Compute the total area in Ha
 * (Floor to int)
 * @param selectedFields
 * @returns
 */
export const computeTotalAreaHa = (selectedFields: PixagriField[]): number => {
  let count: number = 0;
  selectedFields.forEach((field) => {
    count += computeAreaHa(field, false);
  });
  count = Math.floor(count);
  if (count === 0 && selectedFields.length) {
    count = 1;
  }
  return count;
};

export const getFieldEditValidationSchema = (service: ServiceId): ObjectSchema => {
  switch (service) {
    case ServiceId.IRRIGATION:
      return yup.object().shape({
        ...baseValidationSchema(),
        rotation: yup.object().shape({
          ...irrigationValidationSchema(),
        }),
      });
    case ServiceId.VEGETATION:
      return yup.object().shape({
        ...baseValidationSchema(),
        rotation: yup.object().shape({
          ...irrigationValidationSchema(),
        }),
      });
    case ServiceId.WEATHER:
      return yup.object().shape({
        ...baseValidationSchema(),
        rotation: yup.object().shape({
          ...irrigationValidationSchema(),
        }),
      });
    case ServiceId.NITROGEN:
      return yup.object().shape({
        ...baseValidationSchema(),
        cover: yup.object().shape({
          ...nitrogenValidationSchema(),
        }),
      });
    case ServiceId.VISIO_CROP:
      return yup.object().shape({
        ...baseValidationSchema(),
        visioCropFieldUse: yup.object().shape({
          ...visioCropValidationSchema(),
        }),
      });
    default:
      return yup.object().shape(baseValidationSchema());
  }
};

export function getFieldCreationMap(
  field: PixagriField,
  campaignId: number | null,
  farmersList: AllowedUsersModel[],
  addRotationOrCover?: boolean,
) {
  let currentRotation: FieldIrrigationService | null = null;
  if (campaignId && field.irrigation && field.irrigation.length) {
    const rotations = field.irrigation.filter((rot) => rot.campaign_id === campaignId);
    if (rotations && rotations.length) {
      if (rotations.length > 1) {
        rotations.sort(
          (a, b) => DateTime.fromISO(b.sowing_date).toMillis() - DateTime.fromISO(a.sowing_date).toMillis(),
        );
      }
      currentRotation = rotations[0];
    }
  }
  let currentCover: FieldNitrogenService | null = null;
  if (campaignId && field.nitrogen && field.nitrogen.length) {
    currentCover = field.nitrogen.find((cover) => cover.campaign_id === campaignId) || null;
  }
  let irrigationCost: number | string = '';
  if (field?.farmer_id) {
    const farmer = farmersList.find((farmer) => farmer.farmer_id === field?.farmer_id);
    irrigationCost = farmer?.irrigation_cost ?? '';
  }

  let currentVisioCropFieldUse: FieldVisioCropServiceModel<any> | null = null;
  if (campaignId && field.visiocrop && field.visiocrop.length) {
    currentVisioCropFieldUse = field.visiocrop.find((visioCrop) => visioCrop.campaign_id === campaignId) || null;
  }
  const fieldEdit: FieldCreation = {
    irrigation_cost: irrigationCost,
    max_irrigation_dose: field.max_irrigation_dose ?? '',
    irrigation_cycle_duration: field.irrigation_cycle_duration ?? '',
    field_thresholds: {
      ...field.field_thresholds,
    },
    farmer_id: field.farmer_id ?? undefined,
    station_ids:
      field.connected_stations && field.connected_stations.length > 0
        ? field.connected_stations.map((station) => station.station_id)
        : null,
    field_id: field.field_id,
    field_name: field.field_name,
    comments: field.comments,
    soil_id: field.soil_id,
    geometry: field.geometry,
    group: 'group',
    rotation:
      currentRotation && !addRotationOrCover
        ? {
            rotation_id: currentRotation.rotation_id,
            sowing_date: currentRotation.sowing_date,
            harvest_date: currentRotation.harvest_date,
            crop_id: currentRotation.crop_id,
            sys_irrigation_id: currentRotation.sys_irrigation_id,
            yield: currentRotation.yield,
          }
        : BASE_ROTATION_CREATION,
    cover:
      currentCover && !addRotationOrCover
        ? {
            covercrop_id: currentCover.covercrop_id,
            sowing_date: currentCover.sowing_date,
            harvest_date: currentCover.harvest_date,
            destruction_date: currentCover.destruction_date,
            residue_management: currentCover.residue_management,
            covercrop_exploitation: currentCover.covercrop_exploitation,
            target_n_quantity: currentCover.target_n_quantity,
            covercrop_composition: currentCover.covercrop_composition,
          }
        : BASE_COVER_CREATION,
    visioCropFieldUse:
      currentVisioCropFieldUse && !addRotationOrCover
        ? {
            id: currentVisioCropFieldUse.id,
            crop_id: currentVisioCropFieldUse.crop_id,
            harvest_date: currentVisioCropFieldUse.harvest_date || '',
            sowing_date: currentVisioCropFieldUse.sowing_date,
            cropping_system_id: currentVisioCropFieldUse.cropping_system_id,
            processing_engine_ref: currentVisioCropFieldUse.processing_engine_ref,
            parameters: currentVisioCropFieldUse.parameters,
          }
        : BASE_VISIOCROP_FIELD_USE,
  };
  return fieldEdit;
}

export function getFieldName(fieldId: number | string, fieldsData: PixagriField[]) {
  const firstCampaignFound = fieldsData.find(
    (field) => field.field_id === (typeof fieldId === 'string' ? parseInt(fieldId) : fieldId),
  );
  if (firstCampaignFound) {
    return firstCampaignFound.field_name;
  } else {
    log.error(`Field ${fieldId} not found in any campaign`);
  }
  return fieldId.toString();
}

export function getFieldNameFromRotationId(
  rotationId: number | string,
  fieldsData: PixagriField[],
  dateCampaign?: boolean,
) {
  const rotation = getRotation(rotationId, fieldsData);
  if (rotation) {
    const fieldId = rotation.field_id;
    const fieldName = getFieldName(fieldId, fieldsData);
    if (!dateCampaign) {
      return `${fieldName}`;
    } else {
      const startYear = DateTime.fromISO(rotation.campaign_start).get('year');
      const endYear = DateTime.fromISO(rotation.campaign_end).get('year');
      return `${fieldName} (${startYear} - ${endYear})`;
    }
  }
  return rotationId;
}

/**
 * Check if field has a subscription for a service in the campaign
 *
 * @param service
 * @param campaignId
 * @param field
 * @returns
 */
export const isFieldSubscribe = (service: ServiceId | null, campaignId: number, field: PixagriField) => {
  if (!service) {
    return false;
  }
  switch (service) {
    case ServiceId.IRRIGATION:
      if (field.subscriptions.irrigation && field.subscriptions.irrigation.length) {
        const subscription = field.subscriptions.irrigation.find(
          (subs) => subs.campaign_id === campaignId && isSubscriptionValid(subs),
        );
        if (subscription) {
          return true;
        }
      }
      return false;
    case ServiceId.VEGETATION:
      if (field.subscriptions.vegetation && field.subscriptions.vegetation.length) {
        const subscription = field.subscriptions.vegetation.find((subs) => subs.campaign_id === campaignId);
        if (subscription) {
          return true;
        }
      }
      return false;
    case ServiceId.CARBON:
      if (field.subscriptions.carbon && field.subscriptions.carbon.length) {
        const subscription = field.subscriptions.carbon.find(
          (subs) => subs.campaign_id === campaignId && isSubscriptionValid(subs),
        );
        if (subscription) {
          return true;
        }
      }
      return false;
    case ServiceId.NITROGEN:
      if (field.subscriptions.nitrogen && field.subscriptions.nitrogen.length) {
        const subscription = field.subscriptions.nitrogen.find(
          (subs) => subs.campaign_id === campaignId && isSubscriptionValid(subs),
        );
        if (subscription) {
          return true;
        }
      }
      return false;
    case ServiceId.WEATHER:
      if (field.subscriptions.weather && field.subscriptions.weather.length) {
        const subscription = field.subscriptions.weather.find(
          (subs) => subs.campaign_id === campaignId && isSubscriptionValid(subs),
        );
        if (subscription) {
          return true;
        }
      }
      return false;
    case ServiceId.VISIO_CROP:
      return true;
    default:
      return false;
  }
};

export const isFieldComplete = (service: ServiceId, campaignId: number, field: PixagriField) => {
  switch (service) {
    case ServiceId.IRRIGATION:
      if (field.irrigation && field.irrigation.length) {
        const selectedRotation = field.irrigation.find((rot) => rot.campaign_id === campaignId);
        if (selectedRotation) {
          return true;
        }
      }
      return false;
    case ServiceId.VEGETATION:
      if (field.vegetation && field.vegetation.length) {
        const selectedVegetation = field.vegetation.find((veg) => veg.campaign_id === campaignId);
        if (selectedVegetation) {
          return true;
        }
      }
      return false;
    case ServiceId.CARBON:
      return true;
    case ServiceId.NITROGEN:
      if (field.nitrogen && field.nitrogen.length) {
        const selectedCover = field.nitrogen.find((cover) => cover.campaign_id === campaignId);
        if (selectedCover) {
          return true;
        }
      }
      return false;
    case ServiceId.VISIO_CROP:
      if (field.visiocrop && field.visiocrop.length) {
        const selectedVisioCrop = field.visiocrop.find((visioCrop) => visioCrop.campaign_id === campaignId);
        if (selectedVisioCrop) {
          return true;
        }
      }
      return false;
    case ServiceId.WEATHER:
      if (field.irrigation && field.irrigation.length) {
        const selectedRotation = field.irrigation.find((rot) => rot.campaign_id === campaignId);
        if (selectedRotation) {
          return true;
        }
      }
      return false;
    default:
      return false;
  }
};

async function getProducers(token: string): Promise<AllowedUsersModel[]> {
  try {
    const header = new Headers();
    header.append('Authorization', `Bearer ${token}`);
    header.append('accept', `application/json`);
    const requestOptions: RequestInit = {
      headers: header,
      redirect: 'follow',
    };
    const getPoducerUrl = `${wagoNestApiUrl}/users`;
    const req = fetch(getPoducerUrl, requestOptions);
    const response = await req;
    if (response.status < 300) {
      const data = (await response.json()) as AllowedUsersModel[];
      return data;
    }
    throw new Error(`Error while fetching fields, ${response.status}, ${response.statusText}`);
  } catch (error: any) {
    //TODO: Better handle error
    logger.error(error);
    return [] as AllowedUsersModel[];
  }
}

export async function getSortedProducers(token: string, user: AuthUser) {
  const producers = await getProducers(token);
  const sortedAllowedUsers = producers.sort((a, b) =>
    a.farmer_name?.toLowerCase().localeCompare(b.farmer_name?.toLowerCase()),
  );
  const reorderedAllowedUsers = [
    ...sortedAllowedUsers.filter((u) => u.uid === user?.uid),
    ...sortedAllowedUsers.filter((u) => u.uid !== user?.uid),
  ];

  return reorderedAllowedUsers;
}
